// ---------------------------------------------------------------------------
// General settings — GET / PUT /v1/settings/general
//
// The full shape returned by GET and accepted by PUT. The PUT payload doesn't
// include the read-only system fields (createdAt / updatedAt / deletedAt /
// __type / signupBonusAmount) — those are derived/managed server-side.
// ---------------------------------------------------------------------------

export type GeneralSettingsStatus = "ACTIVE" | "INACTIVE";

/** Lower-case enum slugs returned by the API for provider fields. */
export type SettingsProvider = "safe_haven" | (string & {});

/** Known options surfaced in admin dropdowns. Backend may accept more — we
 *  intentionally keep the type open via `(string & {})` so unknown values
 *  returned by GET still parse, and we just render them prettified. */
export const PAYMENT_PROVIDER_OPTIONS: { label: string; value: SettingsProvider }[] = [
  { label: "Safe Haven", value: "safe_haven" },
];

export const VERIFY_PROVIDER_OPTIONS: { label: string; value: SettingsProvider }[] = [
  { label: "Safe Haven", value: "safe_haven" },
];

export const SETTINGS_STATUS_OPTIONS: {
  label: string;
  value: GeneralSettingsStatus;
}[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

export interface GeneralSettings {
  // System metadata (read-only)
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  __type?: string;

  // Lifecycle
  status: GeneralSettingsStatus;

  // Durations (stored as seconds)
  paymentDuration: number;
  disputeAppealDuration: number;
  acceptInitialOrderDuration: number;
  acceptOrderDuration: number;
  requestReturnDuration: number;
  dispatchOrderWaitingDuration: number;
  buyerRequestBatchTimeout: number;
  buyerRequestExpiryTimeout: number;

  // Providers
  defaultPaymentProvider: SettingsProvider;
  defaultBVNVerifyProvider: SettingsProvider;
  defaultNINVerifyProvider: SettingsProvider;
  defaultCACVerifyProvider: SettingsProvider;

  // Marketing program
  referralBonusAmount: number;
  marketerMinTransactionAmount: number;
  marketerPurchasesNeeded: number;
  marketerTotalRewardAmount: number;
  isMarketerRegistrationOpen: boolean;

  // Coupons & rewards
  /**
   * Server-managed value (string with two decimals like "500000.00"). Not in
   * the PUT request body — admin can view it here but updates happen via a
   * different endpoint / pipeline. Tolerant string|number for backend drift.
   */
  signupBonusAmount: string | number;
  enableSignupBonusCoupon: boolean;
  enableFirstOrderDiscountCoupon: boolean;

  // Platform settings
  supportPhoneNumber: string;
  vendorIncentivePercentage: number;
  buyerDiscountPercentage: number;
  numberOfVideoUploadsAllowed: number;
}

/**
 * Subset of `GeneralSettings` that can be sent in a PUT. Read-only fields are
 * stripped. `id` is required for the URL path.
 */
export type UpdateGeneralSettingsPayload = Omit<
  GeneralSettings,
  "createdAt" | "updatedAt" | "deletedAt" | "__type" | "signupBonusAmount"
>;
